/**
 * Currency data with codes, names, and country codes for flags
 * Comprehensive list of all major currencies supported by XE.com API
 */
export interface Currency {
  code: string
  name: string
  countryCode: string // ISO 3166-1 alpha-2 country code for flag
}

/**
 * Popular currencies (most commonly used)
 */
export const POPULAR_CURRENCIES: string[] = [
  'USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'CNY', 'INR', 'ZAR'
]

/**
 * Complete list of supported currencies with metadata
 * Includes all major world currencies supported by XE.com API
 */
export const CURRENCIES: Currency[] = [
  // Major currencies
  { code: 'USD', name: 'US Dollar', countryCode: 'US' },
  { code: 'EUR', name: 'Euro', countryCode: 'EU' },
  { code: 'GBP', name: 'British Pound', countryCode: 'GB' },
  { code: 'JPY', name: 'Japanese Yen', countryCode: 'JP' },
  { code: 'AUD', name: 'Australian Dollar', countryCode: 'AU' },
  { code: 'CAD', name: 'Canadian Dollar', countryCode: 'CA' },
  { code: 'CHF', name: 'Swiss Franc', countryCode: 'CH' },
  { code: 'CNY', name: 'Chinese Yuan', countryCode: 'CN' },
  { code: 'INR', name: 'Indian Rupee', countryCode: 'IN' },
  { code: 'ZAR', name: 'South African Rand', countryCode: 'ZA' },
  
  // Asia Pacific
  { code: 'NZD', name: 'New Zealand Dollar', countryCode: 'NZ' },
  { code: 'SGD', name: 'Singapore Dollar', countryCode: 'SG' },
  { code: 'HKD', name: 'Hong Kong Dollar', countryCode: 'HK' },
  { code: 'KRW', name: 'South Korean Won', countryCode: 'KR' },
  { code: 'THB', name: 'Thai Baht', countryCode: 'TH' },
  { code: 'MYR', name: 'Malaysian Ringgit', countryCode: 'MY' },
  { code: 'IDR', name: 'Indonesian Rupiah', countryCode: 'ID' },
  { code: 'PHP', name: 'Philippine Peso', countryCode: 'PH' },
  { code: 'VND', name: 'Vietnamese Dong', countryCode: 'VN' },
  { code: 'TWD', name: 'Taiwan Dollar', countryCode: 'TW' },
  { code: 'PKR', name: 'Pakistani Rupee', countryCode: 'PK' },
  { code: 'BDT', name: 'Bangladeshi Taka', countryCode: 'BD' },
  { code: 'LKR', name: 'Sri Lankan Rupee', countryCode: 'LK' },
  { code: 'NPR', name: 'Nepalese Rupee', countryCode: 'NP' },
  { code: 'MMK', name: 'Myanmar Kyat', countryCode: 'MM' },
  { code: 'KHR', name: 'Cambodian Riel', countryCode: 'KH' },
  { code: 'LAK', name: 'Lao Kip', countryCode: 'LA' },
  { code: 'MNT', name: 'Mongolian Tugrik', countryCode: 'MN' },
  
  // Middle East
  { code: 'AED', name: 'UAE Dirham', countryCode: 'AE' },
  { code: 'SAR', name: 'Saudi Riyal', countryCode: 'SA' },
  { code: 'ILS', name: 'Israeli Shekel', countryCode: 'IL' },
  { code: 'JOD', name: 'Jordanian Dinar', countryCode: 'JO' },
  { code: 'KWD', name: 'Kuwaiti Dinar', countryCode: 'KW' },
  { code: 'BHD', name: 'Bahraini Dinar', countryCode: 'BH' },
  { code: 'OMR', name: 'Omani Rial', countryCode: 'OM' },
  { code: 'QAR', name: 'Qatari Riyal', countryCode: 'QA' },
  { code: 'IRR', name: 'Iranian Rial', countryCode: 'IR' },
  { code: 'IQD', name: 'Iraqi Dinar', countryCode: 'IQ' },
  { code: 'LBP', name: 'Lebanese Pound', countryCode: 'LB' },
  { code: 'YER', name: 'Yemeni Rial', countryCode: 'YE' },
  
  // Europe
  { code: 'SEK', name: 'Swedish Krona', countryCode: 'SE' },
  { code: 'NOK', name: 'Norwegian Krone', countryCode: 'NO' },
  { code: 'DKK', name: 'Danish Krone', countryCode: 'DK' },
  { code: 'PLN', name: 'Polish Zloty', countryCode: 'PL' },
  { code: 'CZK', name: 'Czech Koruna', countryCode: 'CZ' },
  { code: 'HUF', name: 'Hungarian Forint', countryCode: 'HU' },
  { code: 'RON', name: 'Romanian Leu', countryCode: 'RO' },
  { code: 'BGN', name: 'Bulgarian Lev', countryCode: 'BG' },
  { code: 'HRK', name: 'Croatian Kuna', countryCode: 'HR' },
  { code: 'RSD', name: 'Serbian Dinar', countryCode: 'RS' },
  { code: 'BAM', name: 'Bosnia-Herzegovina Mark', countryCode: 'BA' },
  { code: 'MKD', name: 'Macedonian Denar', countryCode: 'MK' },
  { code: 'ALL', name: 'Albanian Lek', countryCode: 'AL' },
  { code: 'ISK', name: 'Icelandic Krona', countryCode: 'IS' },
  { code: 'RUB', name: 'Russian Ruble', countryCode: 'RU' },
  { code: 'UAH', name: 'Ukrainian Hryvnia', countryCode: 'UA' },
  { code: 'BYN', name: 'Belarusian Ruble', countryCode: 'BY' },
  { code: 'MDL', name: 'Moldovan Leu', countryCode: 'MD' },
  { code: 'GEL', name: 'Georgian Lari', countryCode: 'GE' },
  { code: 'AMD', name: 'Armenian Dram', countryCode: 'AM' },
  { code: 'AZN', name: 'Azerbaijani Manat', countryCode: 'AZ' },
  { code: 'KZT', name: 'Kazakhstani Tenge', countryCode: 'KZ' },
  { code: 'TRY', name: 'Turkish Lira', countryCode: 'TR' },
  
  // Americas
  { code: 'MXN', name: 'Mexican Peso', countryCode: 'MX' },
  { code: 'BRL', name: 'Brazilian Real', countryCode: 'BR' },
  { code: 'ARS', name: 'Argentine Peso', countryCode: 'AR' },
  { code: 'CLP', name: 'Chilean Peso', countryCode: 'CL' },
  { code: 'COP', name: 'Colombian Peso', countryCode: 'CO' },
  { code: 'PEN', name: 'Peruvian Sol', countryCode: 'PE' },
  { code: 'UYU', name: 'Uruguayan Peso', countryCode: 'UY' },
  { code: 'PYG', name: 'Paraguayan Guaraní', countryCode: 'PY' },
  { code: 'BOB', name: 'Bolivian Boliviano', countryCode: 'BO' },
  { code: 'VES', name: 'Venezuelan Bolívar', countryCode: 'VE' },
  { code: 'GTQ', name: 'Guatemalan Quetzal', countryCode: 'GT' },
  { code: 'HNL', name: 'Honduran Lempira', countryCode: 'HN' },
  { code: 'NIO', name: 'Nicaraguan Córdoba', countryCode: 'NI' },
  { code: 'CRC', name: 'Costa Rican Colón', countryCode: 'CR' },
  { code: 'PAB', name: 'Panamanian Balboa', countryCode: 'PA' },
  { code: 'DOP', name: 'Dominican Peso', countryCode: 'DO' },
  { code: 'JMD', name: 'Jamaican Dollar', countryCode: 'JM' },
  { code: 'BBD', name: 'Barbadian Dollar', countryCode: 'BB' },
  { code: 'BZD', name: 'Belize Dollar', countryCode: 'BZ' },
  { code: 'TTD', name: 'Trinidad & Tobago Dollar', countryCode: 'TT' },
  { code: 'XCD', name: 'East Caribbean Dollar', countryCode: 'AG' },
  
  // Africa
  { code: 'EGP', name: 'Egyptian Pound', countryCode: 'EG' },
  { code: 'NGN', name: 'Nigerian Naira', countryCode: 'NG' },
  { code: 'KES', name: 'Kenyan Shilling', countryCode: 'KE' },
  { code: 'GHS', name: 'Ghanaian Cedi', countryCode: 'GH' },
  { code: 'ETB', name: 'Ethiopian Birr', countryCode: 'ET' },
  { code: 'TZS', name: 'Tanzanian Shilling', countryCode: 'TZ' },
  { code: 'UGX', name: 'Ugandan Shilling', countryCode: 'UG' },
  { code: 'RWF', name: 'Rwandan Franc', countryCode: 'RW' },
  { code: 'BWP', name: 'Botswana Pula', countryCode: 'BW' },
  { code: 'NAD', name: 'Namibian Dollar', countryCode: 'NA' },
  { code: 'MZN', name: 'Mozambican Metical', countryCode: 'MZ' },
  { code: 'MAD', name: 'Moroccan Dirham', countryCode: 'MA' },
  { code: 'TND', name: 'Tunisian Dinar', countryCode: 'TN' },
  { code: 'DZD', name: 'Algerian Dinar', countryCode: 'DZ' },
  { code: 'LYD', name: 'Libyan Dinar', countryCode: 'LY' },
  { code: 'SDG', name: 'Sudanese Pound', countryCode: 'SD' },
  { code: 'AOA', name: 'Angolan Kwanza', countryCode: 'AO' },
  { code: 'ZMW', name: 'Zambian Kwacha', countryCode: 'ZM' },
  { code: 'MWK', name: 'Malawian Kwacha', countryCode: 'MW' },
  { code: 'ZWL', name: 'Zimbabwean Dollar', countryCode: 'ZW' },
  { code: 'XOF', name: 'West African CFA Franc', countryCode: 'SN' },
  { code: 'XAF', name: 'Central African CFA Franc', countryCode: 'CM' },
  
  // Other
  { code: 'FJD', name: 'Fijian Dollar', countryCode: 'FJ' },
  { code: 'PGK', name: 'Papua New Guinean Kina', countryCode: 'PG' },
  { code: 'SBD', name: 'Solomon Islands Dollar', countryCode: 'SB' },
  { code: 'VUV', name: 'Vanuatu Vatu', countryCode: 'VU' },
  { code: 'WST', name: 'Samoan Tala', countryCode: 'WS' },
  { code: 'TOP', name: 'Tongan Paʻanga', countryCode: 'TO' },
  { code: 'XPF', name: 'CFP Franc', countryCode: 'PF' },
]

/**
 * Get currency by code
 */
export function getCurrencyByCode(code: string): Currency | undefined {
  return CURRENCIES.find(c => c.code === code)
}

/**
 * Get flag URL for a country code
 * Using flagcdn.com for high-quality flags
 */
export function getFlagUrl(countryCode: string, size: 'w20' | 'w40' | 'w80' | 'w160' | 'w320' = 'w40'): string {
  // Handle special cases
  if (countryCode === 'EU') {
    return `https://flagcdn.com/${size}/eu.png`
  }
  return `https://flagcdn.com/${size}/${countryCode.toLowerCase()}.png`
}

/**
 * Get flag emoji for a country code (fallback)
 */
export function getFlagEmoji(countryCode: string): string {
  // Handle special cases
  if (countryCode === 'EU') {
    return '🇪🇺'
  }
  
  // Convert country code to flag emoji
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}
